import { ApolloLink, split, HttpLink, createHttpLink } from '@apollo/client';
import { ApolloClient } from '@apollo/client-integration-nextjs';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';

import { resolvers, createCache } from 'link-state';
import {
  getUserFromCookie,
  getUserAccessTokenCookie,
  getUserRefreshTokenCookie,
  setUserAccessTokenCookie,
  setUserRefreshTokenCookie,
} from 'commons/localStorage';
import isDevServer from 'commons/isDevServer';
import isLocalhost from 'commons/isLocalhost';
import getAlexandriaUrl from 'commons/getAlexandriaUrl';

import isClient from 'commons/isClient';
import getEnvValue from '@/joystagram-components/commons/getEnvValue';
import {
  classifyFetchFailure,
  FETCH_WITH_RETRY_ERROR_PREFIX,
} from '@/utils/classifyFetchFailure';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import { logError } from '@/utils/logger.mjs';
import getCacheStrategy from '@/utils/server/getCacheStrategy';

const makeApolloClient = logoutClient => () => {
  const cache = createCache();
  let refreshTokenPromise = null;

  // custom request fetcher for refresh token
  const requestFetch = async (uri, options) => {
    try {
      // Determine cache strategy based on request context
      const cacheStrategy = getCacheStrategy(options, 'force-cache');

      const response = await fetchWithRetry(uri, {
        ...options,
        cache: cacheStrategy,
        revalidate: 3600,
      });

      if (!response.ok) {
        return response;
      }

      const responseClone = response.clone(); // Clone only when the response body will be read
      const json = await response.json();
      const finalExtensions =
        json && Array.isArray(json) ? json[0].extensions : json.extensions;
      // If sent JWToken is expired
      if (
        (finalExtensions && finalExtensions.meta.tokenExpired) ||
        (getUserFromCookie() &&
          getUserRefreshTokenCookie() &&
          !getUserAccessTokenCookie())
      ) {
        if (!refreshTokenPromise) {
          const user = getUserFromCookie();
          const refreshToken = getUserRefreshTokenCookie();
          if (user && refreshToken) {
            refreshTokenPromise = fetchWithRetry(getAlexandriaUrl(), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                operationName: 'refreshToken',
                query: `mutation refreshToken($input: RefreshTokenInput) {
                  refreshToken(input: $input) {
                    error
                    access_token
                    refresh_token
                  }
                }`,
                variables: {
                  input: {
                    userId: user.id,
                    userEmail: user.email,
                    userFirstName: user.first_name,
                    userLastName: user.last_name,
                    userGroupId: user.group_id,
                    refreshToken: refreshToken,
                  },
                },
              }),
              cache: 'no-store', // Don't cache token refresh requests
            }).then(response => {
              if (response.ok) {
                return response.json().then(json => {
                  if (json && json.data && json.data.refreshToken) {
                    const {
                      access_token,
                      refresh_token,
                    } = json.data.refreshToken;
                    if (access_token && refresh_token) {
                      setUserRefreshTokenCookie(refresh_token);
                      setUserAccessTokenCookie(access_token);
                      return access_token;
                    } else {
                      logoutClient();
                    }
                  } else {
                    logoutClient();
                  }
                });
              } else {
                logoutClient();
              }
            });
          } else {
            logoutClient();
          }
        }
        if (refreshTokenPromise) {
          return refreshTokenPromise.then(async newAccessToken => {
            refreshTokenPromise = null;
            options.headers.Authorization = newAccessToken;
            // Determine cache strategy based on request context
            const cacheStrategy = getCacheStrategy(options, 'force-cache');
            try {
              return await fetchWithRetry(uri, {
                ...options,
                cache: cacheStrategy,
                revalidate: 3600,
              });
            } catch (err) {
              if (err && err.name === 'AbortError') {
                return new Response(null, {
                  status: 499,
                  statusText: 'Client Closed Request',
                });
              }
              throw err;
            }
          });
        }
      }
      return responseClone;
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return new Response(null, {
          status: 499,
          statusText: 'Client Closed Request',
        });
      }
      throw err;
    }
  };

  const errorLink = new onError(
    ({ graphQLErrors, networkError, operation }) => {
      try {
        if (graphQLErrors) {
          graphQLErrors.forEach(err => {
            logError(
              new Error(
                `GraphQL Error${
                  operation?.operationName
                    ? ': ' + operation?.operationName
                    : ''
                }${err?.message ? ': ' + err?.message : ''}`,
                { cause: err }
              )
            );
          });
        }

        if (networkError) {
          const netMsg = networkError?.message || '';
          if (netMsg.startsWith(FETCH_WITH_RETRY_ERROR_PREFIX)) {
            return;
          }

          const {
            fetchFailureClass,
            causeCode,
            causeChainSummary,
          } = classifyFetchFailure(
            networkError,
            networkError.statusCode != null
              ? networkError.statusCode
              : undefined
          );
          const cause = networkError?.cause || networkError;

          logError(
            new Error(`Network error${netMsg ? ': ' + netMsg : ''}`, {
              cause: networkError,
              context: operation,
            }),
            {
              graphql_operation: operation?.operationName || 'unknown',
              fetchFailureClass,
            },
            {
              underlying_error: cause?.name,
              underlying_message: cause?.message,
              causeCode: causeCode || undefined,
              causeChainSummary,
            }
          );
        }
      } catch (err) {
        // If an error occurs while reporting errors to Sentry, just output to logs instead
        console.error(
          new Error('Error reporting Apollo Client error to Sentry', {
            cause: err,
          })
        );
      }
    }
  );

  // Add Authentication Header to Apollo client request
  const authLink = new ApolloLink((operation, forward) => {
    const accessToken = getUserAccessTokenCookie();

    if (accessToken) {
      const headers = {};
      if (accessToken && accessToken !== 'undefined') {
        headers['Authorization'] = accessToken;
      }
      if (Object.keys(headers).length > 0) {
        operation.setContext(({ headers: previousHeaders }) => ({
          headers: { ...previousHeaders, ...headers },
        }));
      }
    }

    return forward(operation);
  });

  let httpLinks = [],
    batchHttpLinks = [];
  let apolloConfig = {};

  const shouldCreateBatchHttpLink = isClient();
  if (shouldCreateBatchHttpLink) {
    // Enables batching of all queries till noBatch context value is encountered
    const batchHttpLink = new BatchHttpLink({
      uri: getAlexandriaUrl(),
      credentials: 'include',
      fetch: requestFetch,
      batchMax: 1,
    });
    httpLinks = ApolloLink.from([
      errorLink,
      authLink,
      new HttpLink({
        uri: getAlexandriaUrl(),
        credentials: 'include',
        fetch: requestFetch,
      }),
    ]);
    batchHttpLinks = ApolloLink.from([errorLink, authLink, batchHttpLink]);
    apolloConfig = {
      assumeImmutableResults: true,
      link: split(
        operation => operation.getContext().noBatch === true,
        httpLinks,
        batchHttpLinks
      ),
      cache,
      connectToDevTools:
        getEnvValue('NEXT_PUBLIC_BUILD_MODE') === 'development',
      // shouldBatch: true,
      resolvers,
    };
  } else {
    apolloConfig = {
      assumeImmutableResults: true,
      link: ApolloLink.from([
        errorLink,
        authLink,
        createHttpLink({
          uri: getAlexandriaUrl(),
          credentials: 'same-origin',
          fetch: requestFetch,
        }),
      ]),
      cache,
      resolvers,
    };
  }

  const client = new ApolloClient({
    ...apolloConfig,
    connectToDevTools: isLocalhost() || isDevServer(),
  });

  return client;
};

export default makeApolloClient;
